import { dayKeyToSyntheticUtcMs, orgDayKey, type DayKey, type EpochMs } from '@/utils/orgCalendar';

export type DailyMetricRow = Readonly<{ series: string; date: string; value: number }>;
export type DailyPoint = Readonly<{ day: DayKey; value: number | null }>;
export type DailySeries = Readonly<{ name: string; points: readonly DailyPoint[] }>;
export type ChartPoint = [x: EpochMs, y: number | null];
export type ChartSeries = { name: string; data: ChartPoint[] };

type BuildArgs = {
  rows: readonly DailyMetricRow[];
  window: readonly DayKey[];
  timezone: string;
};

type ValidRow = DailyMetricRow & { day: DayKey; index: number };

const validRows = ({ rows, window, timezone }: BuildArgs): ValidRow[] => {
  const dayIndexes = new Map(window.map((day, index) => [day, index]));
  const valid: ValidRow[] = [];

  for (const row of rows) {
    if (!row.series.trim() || !Number.isFinite(row.value)) continue;
    const day = orgDayKey(row.date, timezone);
    if (!day) continue;
    const index = dayIndexes.get(day);
    if (index === undefined) continue;
    valid.push({ ...row, day, index });
  }

  return valid;
};

export const buildDailyTotals = (args: BuildArgs): DailySeries[] => {
  const valuesBySeries = new Map<string, number[]>();

  for (const row of validRows(args)) {
    let values = valuesBySeries.get(row.series);
    if (!values) {
      values = args.window.map(() => 0);
      valuesBySeries.set(row.series, values);
    }
    values[row.index] += row.value;
  }

  return Array.from(valuesBySeries, ([name, values]) => ({
    name,
    points: args.window.map((day, index) => ({ day, value: values[index] })),
  }));
};

export const buildDailySnapshots = (args: BuildArgs): { series: DailySeries[]; latestObservedDay: DayKey | null } => {
  const rows = validRows(args);
  const observedIndexes = new Set(rows.map((row) => row.index));
  const valuesBySeries = new Map<string, Array<number | null>>();
  let latestIndex = -1;

  for (const row of rows) {
    latestIndex = Math.max(latestIndex, row.index);
    let values = valuesBySeries.get(row.series);
    if (!values) {
      values = args.window.map((_, index) => (observedIndexes.has(index) ? 0 : null));
      valuesBySeries.set(row.series, values);
    }
    values[row.index] = row.value;
  }

  return {
    series: Array.from(valuesBySeries, ([name, values]) => ({
      name,
      points: args.window.map((day, index) => ({ day, value: values[index] })),
    })),
    latestObservedDay: latestIndex < 0 ? null : args.window[latestIndex],
  };
};

export const toChartSeries = (series: readonly DailySeries[]): ChartSeries[] =>
  series.map(({ name, points }) => ({
    name,
    data: points.map(({ day, value }): ChartPoint => [dayKeyToSyntheticUtcMs(day), value]),
  }));
